import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  getIdTokenResult,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { isAccessAllowed } from "../utils/access";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [claims, setClaims] = useState(null);
  const [claimsReady, setClaimsReady] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileReady, setProfileReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    let unsubAuth = () => {};
    let unsubProfile = () => {};

    const startAuthListener = () => {
      if (!mounted) return;
      unsubAuth = onAuthStateChanged(auth, (u) => {
        unsubProfile();
        unsubProfile = () => {};
        setUser(u || null);
        setReady(true);
        setClaims(null);
        setClaimsReady(false);
        setProfile(null);
        setProfileReady(false);
        if (u) {
          getIdTokenResult(u)
            .then((res) => {
              if (mounted && auth.currentUser?.uid === u.uid) {
                setClaims(res?.claims || {});
                setClaimsReady(true);
              }
            })
            .catch((error) => {
              console.error("Unable to load Firebase auth claims:", error);
              if (mounted && auth.currentUser?.uid === u.uid) setClaimsReady(true);
            });

          try {
            const ref = doc(db, "users", u.uid);
            unsubProfile = onSnapshot(
              ref,
              (snap) => {
                if (mounted && auth.currentUser?.uid === u.uid) {
                  setProfile(snap.exists() ? snap.data() : {});
                  setProfileReady(true);
                }
              },
              (error) => {
                console.error("Unable to load user profile:", error);
                if (mounted && auth.currentUser?.uid === u.uid) setProfileReady(true);
              }
            );
          } catch (error) {
            console.error("Unable to subscribe to user profile:", error);
            if (mounted && auth.currentUser?.uid === u.uid) setProfileReady(true);
          }
        }
      });
    };

    setPersistence(auth, browserLocalPersistence)
      .catch((error) => {
        console.error("Unable to set Firebase auth persistence:", error);
      })
      .finally(startAuthListener);

    return () => {
      mounted = false;
      unsubAuth();
      unsubProfile();
    };
  }, []);


  const login = (email, pass) => signInWithEmailAndPassword(auth, email, pass);
  // Registration is disabled in the app UI. Keep exported for any admin tooling, but not used on Login page.
  const register = (email, pass) => createUserWithEmailAndPassword(auth, email, pass);
  const logout = useCallback(() => signOut(auth), []);

  // Allowed if token claims OR profile indicate access.
  // Fall back to profile role/active if claims haven't refreshed yet.
  const role = (claims?.role || profile?.role || "").toString();
  const active = claims?.active === true || profile?.active === true;
  const mustChangePassword = Boolean(profile?.mustChangePassword);
  const allowed = isAccessAllowed(role, active);

  return <Ctx.Provider value={{ user, ready, claims, claimsReady, profile, profileReady, role, active, mustChangePassword, allowed, login, register, logout }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
